import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Package,
  Check,
  X,
  Download,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  Play,
  RotateCcw,
  History,
  Loader2,
  Github,
  GitCommit,
  Tag,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { api } from '../services/api';
import type { PluginRelease, PluginStats } from '../types';
import clsx from 'clsx';

const PLUGIN_SLUG = 'simple-backup-wp';
const PLUGIN_NAME = 'SimpleBackup WP';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface ReleaseListProps {
  releases: PluginRelease[];
  pluginSlug: string;
}

function ReleaseList({ releases, pluginSlug }: ReleaseListProps) {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const INITIAL_LIMIT = 5;

  const activateMutation = useMutation({
    mutationFn: (version: string) => api.plugins.activateRelease(pluginSlug, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugin-releases', pluginSlug] });
      queryClient.invalidateQueries({ queryKey: ['plugin-stats', pluginSlug] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (version: string) => api.plugins.deactivateRelease(pluginSlug, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugin-releases', pluginSlug] });
      queryClient.invalidateQueries({ queryKey: ['plugin-stats', pluginSlug] });
    },
  });

  if (releases.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Keine Releases vorhanden</p>
        <p className="text-sm mt-2">Klicke auf "Von GitHub abholen" um Releases zu synchronisieren</p>
      </div>
    );
  }

  const displayedReleases = showAll ? releases : releases.slice(0, INITIAL_LIMIT);
  const hasMore = releases.length > INITIAL_LIMIT;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Version
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Kompatibilität
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Downloads
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Datum
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
              Aktionen
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {displayedReleases.map((release) => (
            <tr key={release.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <span className="font-mono font-medium">{release.version}</span>
              </td>
              <td className="px-4 py-3">
                {release.isActive ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    <Check className="w-3 h-3" />
                    Aktiv
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                    <X className="w-3 h-3" />
                    Inaktiv
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                WP {release.requiresWp}+ / PHP {release.requiresPhp}+
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Download className="w-4 h-4" />
                  {release.downloadCount}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatDate(release.releasedAt)}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <a
                    href={api.plugins.getDownloadUrl(pluginSlug, release.version)}
                    download
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    title={`${pluginSlug}-${release.version}.zip herunterladen`}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  {release.isActive ? (
                    <button
                      onClick={() => deactivateMutation.mutate(release.version)}
                      disabled={deactivateMutation.isPending}
                      className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded"
                      title="Deaktivieren"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => activateMutation.mutate(release.version)}
                      disabled={activateMutation.isPending}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                      title="Aktivieren"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className="px-4 py-3 border-t bg-gray-50">
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Weniger anzeigen
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Alle {releases.length} Releases anzeigen
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

interface SiteVersionsProps {
  stats: PluginStats;
  pluginSlug: string;
  releases: PluginRelease[];
}

type SiteSortField = 'projectName' | 'pluginVersion' | 'hasUpdate';
type SortOrder = 'asc' | 'desc';

function SiteVersions({ stats, pluginSlug, releases }: SiteVersionsProps) {
  const queryClient = useQueryClient();
  const [updatingSites, setUpdatingSites] = useState<Set<string>>(new Set());
  const [rollbackSiteId, setRollbackSiteId] = useState<string | null>(null);
  const [selectedRollbackVersion, setSelectedRollbackVersion] = useState<string>('');
  const [sortBy, setSortBy] = useState<SiteSortField>('projectName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const handleSort = (field: SiteSortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Sort sites
  const sortedSites = [...stats.siteVersions].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'projectName':
        comparison = a.projectName.localeCompare(b.projectName);
        break;
      case 'pluginVersion':
        comparison = (a.pluginVersion || '').localeCompare(b.pluginVersion || '');
        break;
      case 'hasUpdate':
        comparison = (a.hasUpdate ? 1 : 0) - (b.hasUpdate ? 1 : 0);
        break;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const updateSiteMutation = useMutation({
    mutationFn: ({ projectId, version }: { projectId: string; version: string }) =>
      api.plugins.updateSite(pluginSlug, projectId, version, true),
    onMutate: ({ projectId }) => {
      setUpdatingSites(prev => new Set(prev).add(projectId));
    },
    onSettled: (_, __, { projectId }) => {
      setUpdatingSites(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['plugin-stats', pluginSlug] });
      queryClient.invalidateQueries({ queryKey: ['plugin-update-logs', pluginSlug] });
    },
  });

  const rollbackSiteMutation = useMutation({
    mutationFn: ({ projectId, version }: { projectId: string; version?: string }) =>
      api.plugins.rollbackSite(pluginSlug, projectId, version),
    onMutate: ({ projectId }) => {
      setUpdatingSites(prev => new Set(prev).add(projectId));
    },
    onSettled: (_, __, { projectId }) => {
      setUpdatingSites(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
      setRollbackSiteId(null);
      setSelectedRollbackVersion('');
      queryClient.invalidateQueries({ queryKey: ['plugin-stats', pluginSlug] });
      queryClient.invalidateQueries({ queryKey: ['plugin-update-logs', pluginSlug] });
    },
  });

  const [updateError, setUpdateError] = useState<string | null>(null);

  const updateAllMutation = useMutation({
    mutationFn: (version: string) => api.plugins.updateAllSites(pluginSlug, version, true),
    onSuccess: () => {
      setUpdateError(null);
      queryClient.invalidateQueries({ queryKey: ['plugin-stats', pluginSlug] });
      queryClient.invalidateQueries({ queryKey: ['plugin-update-logs', pluginSlug] });
    },
    onError: (err: Error) => {
      setUpdateError(err.message);
    },
  });

  const activeReleases = releases.filter(r => r.isActive);
  const latestVersion = stats.latestVersion;
  // Sites that need update OR have no version (unknown)
  const sitesNeedingUpdate = stats.siteVersions.filter(s => s.hasUpdate || !s.pluginVersion);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
        <h3 className="font-medium">Plugin-Versionen der Sites</h3>
        {sitesNeedingUpdate.length > 0 && latestVersion && (
          <button
            onClick={() => {
              if (confirm(`Alle ${sitesNeedingUpdate.length} Sites auf Version ${latestVersion} aktualisieren?`)) {
                updateAllMutation.mutate(latestVersion);
              }
            }}
            disabled={updateAllMutation.isPending}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white',
              'bg-primary-600 hover:bg-primary-700',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {updateAllMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Aktualisiere...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Alle aktualisieren ({sitesNeedingUpdate.length})
              </>
            )}
          </button>
        )}
      </div>

      <div className="p-4 border-b bg-gray-50">
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>{stats.stats.onLatest} aktuell</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span>{stats.stats.needsUpdate} Update verfügbar</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-gray-400" />
            <span>{stats.stats.unknown} unbekannt</span>
          </div>
        </div>
      </div>

      {updateAllMutation.isSuccess && updateAllMutation.data && (
        <div className="p-4 bg-green-50 border-b border-green-200">
          <div className="flex items-center gap-2 text-green-700 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>
              Batch-Update abgeschlossen: {updateAllMutation.data.success} erfolgreich, {updateAllMutation.data.failed} fehlgeschlagen
            </span>
          </div>
        </div>
      )}

      {(updateAllMutation.isError || updateError) && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <XCircle className="w-4 h-4" />
            <span>Update fehlgeschlagen: {updateError || 'Unbekannter Fehler'}</span>
          </div>
        </div>
      )}

      {stats.siteVersions.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          Keine Sites gefunden
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('projectName')}
              >
                <div className="flex items-center gap-1">
                  <span>Site</span>
                  {sortBy === 'projectName' ? (
                    sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronsUpDown className="w-3 h-3 opacity-50" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('pluginVersion')}
              >
                <div className="flex items-center gap-1">
                  <span>Plugin-Version</span>
                  {sortBy === 'pluginVersion' ? (
                    sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronsUpDown className="w-3 h-3 opacity-50" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('hasUpdate')}
              >
                <div className="flex items-center gap-1">
                  <span>Status</span>
                  {sortBy === 'hasUpdate' ? (
                    sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronsUpDown className="w-3 h-3 opacity-50" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedSites.map((site) => (
              <tr key={site.projectId} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link
                    to={`/projects/${site.projectId}`}
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-800"
                  >
                    {site.projectName}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </td>
                <td className="px-4 py-3 font-mono text-sm">
                  {site.pluginVersion || '-'}
                </td>
                <td className="px-4 py-3">
                  {site.pluginVersion ? (
                    site.hasUpdate ? (
                      <span className="inline-flex items-center gap-1 text-yellow-600 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        Update verfügbar
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Aktuell
                      </span>
                    )
                  ) : (
                    <span className="text-gray-400 text-sm">Unbekannt</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {(site.hasUpdate || !site.pluginVersion) && latestVersion && (
                      <button
                        onClick={() => updateSiteMutation.mutate({
                          projectId: site.projectId,
                          version: latestVersion,
                        })}
                        disabled={updatingSites.has(site.projectId)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50"
                        title={`Auf ${latestVersion} aktualisieren`}
                      >
                        {updatingSites.has(site.projectId) ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                        {site.pluginVersion ? 'Update' : 'Installieren'}
                      </button>
                    )}
                    {site.pluginVersion && (
                      <>
                        {rollbackSiteId === site.projectId ? (
                          <div className="flex items-center gap-1">
                            <select
                              value={selectedRollbackVersion}
                              onChange={(e) => setSelectedRollbackVersion(e.target.value)}
                              className="text-xs border rounded px-1 py-1"
                            >
                              <option value="">Version wählen...</option>
                              {activeReleases
                                .filter(r => r.version !== site.pluginVersion)
                                .map(r => (
                                  <option key={r.version} value={r.version}>
                                    {r.version}
                                  </option>
                                ))}
                            </select>
                            <button
                              onClick={() => {
                                if (selectedRollbackVersion) {
                                  rollbackSiteMutation.mutate({
                                    projectId: site.projectId,
                                    version: selectedRollbackVersion,
                                  });
                                }
                              }}
                              disabled={!selectedRollbackVersion || updatingSites.has(site.projectId)}
                              className="p-1 text-orange-600 hover:bg-orange-50 rounded disabled:opacity-50"
                              title="Rollback starten"
                            >
                              <Check className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => {
                                setRollbackSiteId(null);
                                setSelectedRollbackVersion('');
                              }}
                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                              title="Abbrechen"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setRollbackSiteId(site.projectId)}
                            disabled={updatingSites.has(site.projectId)}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-600 hover:bg-orange-50 rounded disabled:opacity-50"
                            title="Rollback"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Rollback
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface UpdateLogsProps {
  pluginSlug: string;
}

function UpdateLogs({ pluginSlug }: UpdateLogsProps) {
  const { data: logs, isLoading } = useQuery({
    queryKey: ['plugin-update-logs', pluginSlug],
    queryFn: () => api.plugins.getUpdateLogs(pluginSlug, undefined, 20),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Keine Update-Logs vorhanden</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Site
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Aktion
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Version
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Datum
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {logs.map((log) => (
            <tr key={log.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                {log.project ? (
                  <Link
                    to={`/projects/${log.projectId}`}
                    className="text-primary-600 hover:text-primary-800"
                  >
                    {log.project.name}
                  </Link>
                ) : (
                  <span className="text-gray-400">Unbekannt</span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className={clsx(
                  'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                  log.action === 'UPDATE' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                )}>
                  {log.action === 'UPDATE' ? (
                    <>
                      <Play className="w-3 h-3" />
                      Update
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-3 h-3" />
                      Rollback
                    </>
                  )}
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-sm">
                {log.fromVersion && (
                  <span className="text-gray-400">{log.fromVersion} → </span>
                )}
                <span>{log.toVersion}</span>
              </td>
              <td className="px-4 py-3">
                {log.status === 'SUCCESS' && (
                  <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Erfolgreich
                  </span>
                )}
                {log.status === 'FAILED' && (
                  <span className="inline-flex items-center gap-1 text-red-600 text-sm" title={log.errorMessage || undefined}>
                    <XCircle className="w-4 h-4" />
                    Fehlgeschlagen
                  </span>
                )}
                {log.status === 'IN_PROGRESS' && (
                  <span className="inline-flex items-center gap-1 text-blue-600 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Läuft...
                  </span>
                )}
                {log.status === 'PENDING' && (
                  <span className="inline-flex items-center gap-1 text-gray-500 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    Wartend
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatDate(log.startedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PluginManagement() {
  const queryClient = useQueryClient();
  const [hasAutoSynced, setHasAutoSynced] = useState(false);

  const { data: releases, isLoading: releasesLoading } = useQuery({
    queryKey: ['plugin-releases', PLUGIN_SLUG],
    queryFn: () => api.plugins.getReleases(PLUGIN_SLUG),
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['plugin-stats', PLUGIN_SLUG],
    queryFn: () => api.plugins.getStats(PLUGIN_SLUG),
  });

  const { data: githubInfo } = useQuery({
    queryKey: ['plugin-github-info', PLUGIN_SLUG],
    queryFn: () => api.plugins.getGithubInfo(PLUGIN_SLUG),
  });

  const syncVersionsMutation = useMutation({
    mutationFn: () => api.plugins.syncVersions(PLUGIN_SLUG),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugin-stats', PLUGIN_SLUG] });
    },
  });

  const syncGithubMutation = useMutation({
    mutationFn: () => api.plugins.syncFromGithub(PLUGIN_SLUG),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugin-releases', PLUGIN_SLUG] });
      queryClient.invalidateQueries({ queryKey: ['plugin-stats', PLUGIN_SLUG] });
      queryClient.invalidateQueries({ queryKey: ['plugin-github-info', PLUGIN_SLUG] });
    },
  });

  // Auto-sync versions if all sites have no version
  useEffect(() => {
    if (
      !hasAutoSynced &&
      !statsLoading &&
      stats &&
      stats.siteVersions.length > 0 &&
      stats.siteVersions.every(s => !s.pluginVersion) &&
      !syncVersionsMutation.isPending
    ) {
      setHasAutoSynced(true);
      syncVersionsMutation.mutate();
    }
  }, [stats, statsLoading, hasAutoSynced, syncVersionsMutation]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Plugin Update Server</h1>
            <p className="text-gray-600 mt-1">
              Verwalte Releases für {PLUGIN_NAME}
            </p>
          </div>
          {stats?.latestVersion && (
            <div className="flex items-center gap-2 px-4 py-2 bg-primary-100 border border-primary-300 rounded-lg">
              <Package className="w-5 h-5 text-primary-600" />
              <div>
                <div className="text-xs text-primary-600 font-medium">Aktuelle Version</div>
                <div className="text-lg font-bold text-primary-700 font-mono">{stats.latestVersion}</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => syncVersionsMutation.mutate()}
            disabled={syncVersionsMutation.isPending}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
              'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <RefreshCw className={clsx('w-4 h-4', syncVersionsMutation.isPending && 'animate-spin')} />
            Site-Versionen sync
          </button>

          <button
            onClick={() => syncGithubMutation.mutate()}
            disabled={syncGithubMutation.isPending}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white',
              'bg-gray-800 hover:bg-gray-900',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <Github className={clsx('w-4 h-4', syncGithubMutation.isPending && 'animate-spin')} />
            {syncGithubMutation.isPending ? 'Synchronisiere...' : 'Von GitHub abholen'}
          </button>
        </div>
      </div>

      {/* GitHub Sync Result Message */}
      {syncGithubMutation.isSuccess && syncGithubMutation.data && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>
              {syncGithubMutation.data.message}
              {syncGithubMutation.data.latest && (
                <span className="ml-2 font-mono">Neueste Version: {syncGithubMutation.data.latest}</span>
              )}
              {syncGithubMutation.data.commit && (
                <span className="ml-2 text-gray-500">Commit: {syncGithubMutation.data.commit}</span>
              )}
            </span>
          </div>
        </div>
      )}

      {syncGithubMutation.isError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <XCircle className="w-4 h-4" />
            <span>GitHub-Synchronisierung fehlgeschlagen</span>
          </div>
        </div>
      )}

      {/* Sync Result Message */}
      {syncVersionsMutation.isSuccess && syncVersionsMutation.data && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>
              Site-Synchronisierung abgeschlossen: {syncVersionsMutation.data.synced} Sites synchronisiert, {syncVersionsMutation.data.failed} fehlgeschlagen
            </span>
          </div>
        </div>
      )}

      {/* GitHub Repository Info */}
      {stats?.githubRepo && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Github className="w-6 h-6 text-gray-700" />
              <div>
                <a
                  href={stats.githubRepo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-gray-900 hover:text-primary-600 flex items-center gap-1"
                >
                  {stats.githubRepo.owner}/{stats.githubRepo.repo}
                  <ExternalLink className="w-3 h-3" />
                </a>
                <p className="text-xs text-gray-500">
                  Releases werden automatisch von GitHub abgerufen
                </p>
              </div>
            </div>
            {githubInfo && (
              <div className="flex items-center gap-6">
                {githubInfo.latestVersion && (
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-green-600" />
                    <div>
                      <div className="text-xs text-gray-500">GitHub Version</div>
                      <div className="font-mono font-medium text-green-700">{githubInfo.latestVersion}</div>
                    </div>
                  </div>
                )}
                {githubInfo.latestCommit && (
                  <div className="flex items-center gap-2">
                    <GitCommit className="w-4 h-4 text-gray-600" />
                    <div>
                      <div className="text-xs text-gray-500">Letzter Commit</div>
                      <div className="font-mono text-sm text-gray-700" title={githubInfo.latestCommitMessage || undefined}>
                        {githubInfo.latestCommit}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Releases</div>
            <div className="text-2xl font-bold text-gray-900">
              {stats.releases.length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Downloads gesamt</div>
            <div className="text-2xl font-bold text-gray-900">
              {stats.totalDownloads}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Sites aktuell</div>
            <div className="text-2xl font-bold text-green-600">
              {stats.stats.onLatest}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Sites mit Update</div>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.stats.needsUpdate}
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {(releasesLoading || statsLoading) && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Releases List */}
      {!releasesLoading && releases && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Releases</h2>
          <ReleaseList releases={releases} pluginSlug={PLUGIN_SLUG} />
        </div>
      )}

      {/* Site Versions */}
      {!statsLoading && stats && releases && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Site-Übersicht</h2>
          <SiteVersions stats={stats} pluginSlug={PLUGIN_SLUG} releases={releases} />
        </div>
      )}

      {/* Update Logs */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Update-Verlauf</h2>
        <UpdateLogs pluginSlug={PLUGIN_SLUG} />
      </div>
    </div>
  );
}
